import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiService } from '../services/apiService';
import { portalService } from '../services/portalService';

const statusStyle = {
  present: { background: '#d4edda', color: '#155724' },
  absent:  { background: '#f8d7da', color: '#721c24' },
  late:    { background: '#fff3cd', color: '#856404' },
};

const ChildrenAttendance = () => {
  const [children, setChildren]     = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [records, setRecords]       = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const dashboard = await portalService.getParentDashboard();
        const kids = dashboard.children || [];
        setChildren(kids);
        if (kids.length) setSelectedId(kids[0].id);
      } catch { setError('Failed to load children.'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const load = async () => {
      try {
        setDataLoading(true);
        const [att, sum] = await Promise.all([
          apiService.get(`attendance/student/${selectedId}`),
          apiService.get(`attendance/summary/${selectedId}`),
        ]);
        setRecords(att || []);
        setSummary(sum || {});
      } catch { setError('Failed to load attendance.'); }
      finally { setDataLoading(false); }
    };
    load();
  }, [selectedId]);

  const child = children.find(c => c.id === selectedId);
  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Attendance {child ? `— ${child.name}` : ''}</h1>
      </div>
      {error && <div className="error-message">{error}</div>}

      {children.length > 1 && (
        <div className="form-group" style={{ maxWidth: 300, marginBottom: '1.5rem' }}>
          <label>Select Child</label>
          <select value={selectedId} onChange={e => setSelectedId(Number(e.target.value))}>
            {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {dataLoading ? <LoadingSpinner message="Loading attendance..." /> : (
        <>
          <div className="stats-grid" style={{ marginBottom: '2rem' }}>
            {[
              { label: 'Present', value: summary?.present ?? 0, color: '#388E3C' },
              { label: 'Absent',  value: summary?.absent  ?? 0, color: '#D32F2F' },
              { label: 'Late',    value: summary?.late    ?? 0, color: '#F57C00' },
              { label: 'Rate',    value: `${summary?.percentage ?? 0}%`, color: (summary?.percentage ?? 0) >= 75 ? '#388E3C' : '#D32F2F' },
            ].map(item => (
              <div key={item.label} className="stat-card">
                <h3>{item.label}</h3>
                <div className="stat-value" style={{ color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Status</th><th>Remark</th></tr></thead>
              <tbody>
                {records.length === 0 ? (
                  <tr><td colSpan={3} className="no-data-message">No attendance records yet.</td></tr>
                ) : records.map(r => (
                  <tr key={r._id || `${r.date}-${r.classId}`}>
                    <td>{new Date(r.date).toLocaleDateString()}</td>
                    <td>
                      <span style={{ ...statusStyle[r.status], padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600 }}>
                        {r.status}
                      </span>
                    </td>
                    <td>{r.remark || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ChildrenAttendance;
